import { __extends } from 'tslib';
import * as React from 'react';
import { classNames, validatePropType } from 'uitk-react-utils';
import { UitkHeadingTags, defaultMenuHeadingTag } from './types.esm.js';
import styles from './uitk-menu.css';

var getClassNameList = classNames.bind(styles);
var PositionType;
(function (PositionType) {
    PositionType["RIGHT"] = "right";
    PositionType["LEFT"] = "left";
})(PositionType || (PositionType = {}));
var UitkMenuContainer = /** @class */ (function (_super) {
    __extends(UitkMenuContainer, _super);
    function UitkMenuContainer(props) {
        var _this = _super.call(this, props) || this;
        _this.state = {
            animationHasEnded: false,
            offset: {
                x: 0,
                y: 0
            }
        };
        _this.autoPositionCallback = _this.autoPositionCallback.bind(_this);
        _this.adjustAlignment = _this.adjustAlignment.bind(_this);
        _this.resetMenuContainer = _this.resetMenuContainer.bind(_this);
        _this.onAnimationEndCallback = _this.onAnimationEndCallback.bind(_this);
        _this.animationEndedStateCallback = _this.animationEndedStateCallback.bind(_this);
        _this.unobserveIntersectionObserver = _this.unobserveIntersectionObserver.bind(_this);
        _this.onMenuContainerClose = _this.onMenuContainerClose.bind(_this);
        return _this;
        // This.resizeObserverHandler = this.resizeObserverHandler.bind(this);
        // this.resizeObserverIsBound = false;
        // this.resizeWidth = 0;
    }
    UitkMenuContainer.prototype.componentDidMount = function () {
        var _a = this.props, autoPosition = _a.autoPosition, isOpen = _a.isOpen;
        isOpen && autoPosition && this.autoPositionObserver(); // Setting up auto position observer here for instances where the menu container is added/removed from DOM as opposed to visibility toggled.
    };
    UitkMenuContainer.prototype.componentDidUpdate = function (prevProps) {
        var _a = this.props, autoPosition = _a.autoPosition, isOpen = _a.isOpen;
        if (isOpen !== prevProps.isOpen) {
            if (isOpen) {
                autoPosition && this.autoPositionObserver(); // Setting up auto position observer here for instances where the menu container is added to the DOM, but visibility is toggled.
            }
            else {
                this.onMenuContainerClose();
            }
        }
    };
    UitkMenuContainer.prototype.componentWillUnmount = function () {
        this.onMenuContainerClose();
    };
    UitkMenuContainer.prototype.onMenuContainerClose = function () {
        this.resetMenuContainer();
        this.unobserveIntersectionObserver();
    };
    UitkMenuContainer.prototype.onAnimationEndCallback = function (e) {
        var animationHasEnded = this.state.animationHasEnded;
        // There are multiple animationEnd callbacks due to there being 2-3 animations applied, this logic ensures animationEndedStateCallback() is only called once.
        !animationHasEnded &&
            e.animationName.match(/menu-grow-/) &&
            this.setState({ animationHasEnded: true }, this.animationEndedStateCallback);
    };
    // Private resizeObserver: any;
    // private resizeObserverIsBound: boolean;
    // private resizeWidth: number;
    // This callback may appear to be doing something similar to adjustAlignment(), but it's actually only required for Safari desktop.
    // Safari desktop doesn't currently support the use of css variables inside keyframe animations. Therefore the updates to --menu-grow-x and --menu-grow-y have no impact in safari desktop.
    // In order to get the auto positioning working in safari desktop, we need to use transform: translate and set css variables for x and y to be used in the translate.
    // There's also a corresponding CSS "hack" to target safari desktop only ln 59 in uitk-menu.scss
    // The reason all of this is done once the animation is complete, is because if we do this during the animation, it causes some jankiness.
    UitkMenuContainer.prototype.animationEndedStateCallback = function () {
        var containerRef = this.props.containerRef;
        // eslint-disable-next-line no-negated-condition
        var viewport = typeof document !== 'undefined' ? document.documentElement : null;
        var windowInnerHeight = window.innerHeight;
        if ((containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) && viewport) {
            var container = containerRef.current;
            var containerBoundingClientRect = container.getBoundingClientRect();
            var viewportBoundingClientRect = viewport.getBoundingClientRect();
            var offsetX = 0;
            var offsetY = 0;
            if (containerBoundingClientRect.right > viewportBoundingClientRect.right) {
                offsetX = -Math.abs(Math.ceil(containerBoundingClientRect.right) - Math.ceil(viewportBoundingClientRect.right));
            }
            else if (containerBoundingClientRect.left < viewportBoundingClientRect.left) {
                offsetX = Math.abs(containerBoundingClientRect.left);
            }
            if (containerBoundingClientRect.bottom > windowInnerHeight) {
                offsetY = -Math.abs(Math.ceil(containerBoundingClientRect.bottom) - Math.ceil(windowInnerHeight));
            }
            if (offsetX || offsetY) {
                container.style.setProperty('--menu-offset-translate-x', "".concat(offsetX, "px"));
                container.style.setProperty('--menu-offset-translate-y', "".concat(offsetY, "px"));
            }
        }
        document.addEventListener('scroll', this.unobserveIntersectionObserver);
        window.addEventListener('resize', this.unobserveIntersectionObserver);
    };
    UitkMenuContainer.prototype.unobserveIntersectionObserver = function () {
        var _a = this.props, autoPosition = _a.autoPosition, containerRef = _a.containerRef;
        if (autoPosition && containerRef.current) {
            this.intersectionObserver && this.intersectionObserver.unobserve(containerRef.current);
            // This.resizeObserver &&
            //   this.resizeObserver.unobserve(this.props.containerRef!.current);
        }
    };
    UitkMenuContainer.prototype.autoPositionObserver = function () {
        var _a = this.props, containerRef = _a.containerRef, intersectionObserverRoot = _a.intersectionObserverRoot;
        if (!(window && window.IntersectionObserver)) {
            return false;
        }
        var thresholdSet = [];
        for (var i = 0.0; i <= 1.0; i += 0.01) {
            thresholdSet.push(i);
        }
        var defaultIntersectionObserverOptions = {
            threshold: thresholdSet
        };
        var intersectionObserverOptions = intersectionObserverRoot
            ? Object.assign(defaultIntersectionObserverOptions, {
                root: document.querySelector(intersectionObserverRoot)
            })
            : defaultIntersectionObserverOptions;
        this.intersectionObserver = new IntersectionObserver(this.autoPositionCallback, intersectionObserverOptions);
        containerRef.current && this.intersectionObserver.observe(containerRef.current);
    };
    UitkMenuContainer.prototype.autoPositionCallback = function (entries) {
        var _this = this;
        entries.forEach(function (entry) {
            var formattedRatio = Math.round(parseFloat(entry.intersectionRatio.toString()) * 100) / 100;
            if (formattedRatio <= 0.99 && entry.intersectionRatio > 0) {
                _this.adjustAlignment(entry);
            }
        });
    };
    UitkMenuContainer.prototype.adjustAlignment = function (entry) {
        // Set offset state to be used as measure for translate: transformX
        // !this.resizeObserverIsBound &&
        //   typeof ResizeObserver !== 'undefined' &&
        //   this.resizeObserverHandler(); // Instantiate a resize observer in case user resizes window or changes orientation
        if (Math.ceil(entry.boundingClientRect.right) > Math.ceil(entry.intersectionRect.right)) {
            var currentOffsetX_1 = Math.ceil(entry.boundingClientRect.right) - Math.ceil(entry.intersectionRect.right);
            this.setState(function (prevState) { return ({
                offset: {
                    x: -Math.abs(prevState.offset.x - currentOffsetX_1),
                    y: prevState.offset.y
                }
            }); });
        }
        else if (Math.ceil(entry.boundingClientRect.left) < Math.ceil(entry.intersectionRect.left)) {
            var currentOffsetX_2 = entry.boundingClientRect.left - entry.intersectionRect.left;
            this.setState(function (prevState) { return ({
                offset: {
                    x: prevState.offset.x - currentOffsetX_2,
                    y: prevState.offset.y
                }
            }); });
        }
        var adjustmentType = this.state.adjustmentType;
        if (Math.ceil(entry.boundingClientRect.bottom) > window.innerHeight) {
            // Checks if the menu container is taller than the viewport
            var currentOffsetY_1 = Math.ceil(entry.boundingClientRect.bottom) - window.innerHeight;
            this.setState(function (prevState) { return ({
                offset: {
                    x: prevState.offset.x,
                    y: 0
                },
                adjustedHeight: entry.boundingClientRect.height - Math.abs(prevState.offset.y - currentOffsetY_1),
                adjustmentType: 'window'
            }); });
        }
        if (Math.ceil(entry.boundingClientRect.bottom) > Math.ceil(entry.intersectionRect.bottom)) {
            // Checks if the menu container is taller than the defined root
            var currentOffsetY_2 = Math.ceil(entry.boundingClientRect.bottom) - Math.ceil(entry.intersectionRect.bottom);
            if (entry.boundingClientRect.height > entry.intersectionRect.height) {
                // Checks if the menu container is taller than the defined root
                this.setState(function (prevState) { return ({
                    offset: {
                        x: prevState.offset.x,
                        y: 0
                    },
                    adjustedHeight: entry.boundingClientRect.height - Math.abs(prevState.offset.y - currentOffsetY_2),
                    adjustmentType: 'container'
                }); });
            }
            else {
                // If the menu container is not taller than the defined root, shifts the menu container up
                this.setState(function (prevState) { return ({
                    offset: {
                        x: prevState.offset.x,
                        y: -Math.abs(prevState.offset.y - currentOffsetY_2)
                    }
                }); });
            }
        }
        else if (adjustmentType === 'window') {
            // Remove window adjustment if no longer needed
            this.setState(function () { return ({
                adjustedHeight: undefined,
                adjustmentType: undefined
            }); });
        }
    };
    UitkMenuContainer.prototype.resetMenuContainer = function () {
        var _a, _b;
        this.setState({
            offset: {
                x: 0,
                y: 0
            },
            animationHasEnded: false
        });
        var containerRef = this.props.containerRef;
        (_a = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _a === void 0 ? void 0 : _a.style.removeProperty('--menu-offset-translate-x');
        (_b = containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) === null || _b === void 0 ? void 0 : _b.style.removeProperty('--menu-offset-translate-y');
        document.removeEventListener('scroll', this.unobserveIntersectionObserver);
        window.removeEventListener('resize', this.unobserveIntersectionObserver);
        // This.resizeObserver &&
        //   this.resizeObserver.unobserve(document.documentElement);
        // this.resizeObserverIsBound = false;
    };
    // Decided not to use in production as of 12/2019 due to ResizeObserver being "Editor's Draft". If the specification becomes CR / REC then this functionality can be re-introduced
    // private resizeObserverHandler() {
    //   this.resizeObserverIsBound = true;
    //   // @ts-ignore
    //   this.resizeObserver = new ResizeObserver(entries => {
    //     for (const entry of entries) {
    //       const resizeCurrentWidth = entry.contentRect.width;
    //       if (this.resizeWidth > 0 && resizeCurrentWidth > this.resizeWidth) {
    //         // If resize width is increasing - re-adjust offset
    //         if (this.props.position === PositionType.RIGHT) {
    //           this.props.containerRef!.current &&
    //             this.setState(
    //               state => ({
    //                 offset:
    //                   state.offset -
    //                   Math.ceil(
    //                     this.props.containerRef!.current!.getBoundingClientRect()
    //                       .left
    //                   )
    //               }),
    //               this.handleMenuContainerOffset
    //             );
    //         } else {
    //           this.props.containerRef!.current &&
    //             this.setState(
    //               state => ({
    //                 offset:
    //                   state.offset +
    //                   (Math.ceil(entry.contentRect.right) -
    //                     Math.ceil(
    //                       this.props.containerRef!.current!.getBoundingClientRect()
    //                         .right
    //                     ))
    //               }),
    //               this.handleMenuContainerOffset
    //             );
    //         }
    //       }
    //       this.resizeWidth = resizeCurrentWidth;
    //     }
    //   });
    //   this.resizeObserver.observe(document.documentElement);
    // }
    UitkMenuContainer.prototype.render = function () {
        var _a;
        var _b = this.props, autoPosition = _b.autoPosition, containerClasses = _b.containerClasses, containerRef = _b.containerRef, children = _b.children, position = _b.position, className = _b.className, isOpen = _b.isOpen, maxHeight = _b.maxHeight, width = _b.width, title = _b.title, allyHeadingLevel = _b.allyHeadingLevel, intersectionObserverRoot = _b.intersectionObserverRoot, showOverTrigger = _b.showOverTrigger;
        var _c = this.state, _d = _c.offset, x = _d.x, y = _d.y, adjustedHeight = _c.adjustedHeight;
        var wrapperClassNames = getClassNameList(className, containerClasses, (_a = {},
            _a["uitk-menu-pos-".concat(position)] = validatePropType(position, PositionType),
            _a['uitk-menu-container-autoposition'] = !!autoPosition,
            _a['uitk-menu-container-text-nowrap'] = !width,
            _a['uitk-menu-container-has-intersection-root-el'] = !!intersectionObserverRoot,
            _a['uitk-menu-container-over-trigger'] = !!showOverTrigger,
            _a));
        var divStyle = {};
        var containerHeight = adjustedHeight || maxHeight;
        if (adjustedHeight && maxHeight) {
            containerHeight = Math.min(adjustedHeight, maxHeight);
        }
        if (containerHeight) {
            divStyle.maxHeight = "".concat(containerHeight, "px");
            divStyle.overflow = 'auto';
        }
        if (width) {
            divStyle.width = "".concat(width, "px");
        }
        if (x || y) {
            divStyle['--menu-grow-x'] = "".concat(x, "px");
            divStyle['--menu-grow-y'] = "".concat(y, "px");
        }
        var HeadingLevel = allyHeadingLevel
            ? UitkHeadingTags.get(allyHeadingLevel)
            : UitkHeadingTags.get(defaultMenuHeadingTag);
        return (React.createElement("div", { className: wrapperClassNames, ref: containerRef, "aria-hidden": !isOpen, style: divStyle, onAnimationEnd: this.onAnimationEndCallback },
            title && React.createElement(HeadingLevel, { className: getClassNameList('uitk-menu-title') }, title),
            children));
    };
    UitkMenuContainer.displayName = 'UitkMenuContainer';
    UitkMenuContainer.defaultProps = {
        position: PositionType.RIGHT
    };
    return UitkMenuContainer;
}(React.Component));

export { PositionType, UitkMenuContainer };
